import {
    AppInstanceSortBy,
    AppInstanceOrderBy,
    encodeAPIAppInstanceListParams
} from "../Models/API/AppInstanceModel"
import {
    JobStatus,
    APIJobListParams,
    JobReviewStatus,
    JobSortBy,
    JobOrderBy,
    APIJobRelabelParams,
    encodeJobCancelAPIParams,
    encodeJobRelabelAPIParams,
    APIJobEnqueueParams,
    encodeJobEnqueueAPIParams,
    encodeJobAPIListParams,
    FeedbackMessageSortBy,
    FeedbackMessageOrderBy,
    APIJobHistoryParams,
    APIJobCountByStatusParams,
    encodeJobCountByStatusAPIParams
} from "../Models/API/JobModel"
import { APIPaymentMethodStatus } from "../Models/API/BillingModel"
import { APIDataModel } from "../Models/API/DataModel"
import { SortExpression } from "../Models/Client/SortExpression"
import {
    APIGroundTruthModel,
    APIGroundTruthType
} from "../Models/API/APIGroundTruthModel"
import { APICollaborationModel } from "../Models/API/CollaboratorModel"
import { ScopedDataAccess } from "../Models/Client/DataAccess"
import { TemplateName } from "../Models/API/AppTemplateModel"
import {
    APITaskListParams,
    encodeTaskAPIListParams
} from "../Models/API/TaskModel"
import { APIWorker } from "../Models/API/Worker"
import { APIWorkerGroup } from "../Models/API/WorkerGroup"
import { APIAppWorker } from "../Models/API/AppWorker"
import { APIAppWorkerGroup } from "../Models/API/AppWorkerGroup"
import { APIOrgMember } from "../Models/API/APIOrgMember"
import {
    APIAppAsyncOperationStatus,
    APIAppAsyncOperationType
} from "../Models/API/APIAppAsyncOperationModel"
import {
    BackofficeCustomerParams,
    encodeBackofficeParams
} from "../Models/API/BackofficeModel"
import { AppDownloadSettings } from "../Models/API/AppSettings"

// TODO: handle options (RequestInit object)
export interface APIEndpointInfo {
    url: string
    /**
     * default to nacelle
     */
    service?:
        | "nacelle"
        | "autoSegmentation"
        | "objectTracking"
        | "backoffice"
        | "clientPulse"
    useAPIKeyIfAvailable?: boolean
    dontRequireAPIKey?: boolean
    requireAuthToken?: boolean
}

interface APIEndpointFnArgs {}

type APIRouteFn<T extends APIEndpointFnArgs> = (args: T) => APIEndpointInfo

interface CheckOnboardingCodeArgs extends APIEndpointFnArgs {
    submitCode: number
}

interface GetJobByIDArgs extends APIEndpointFnArgs {
    jobID: string
}

interface GetTemplateByNameArgs extends APIEndpointFnArgs {
    templateName: string
}

interface GetInstanceByIDArgs extends APIEndpointFnArgs {
    id: string
}

interface GetInstanceListArgs extends APIEndpointFnArgs {
    page: number
    pageSize: number
    sortBy: AppInstanceSortBy
    orderBy: AppInstanceOrderBy
}

interface GetJobListArgs extends APIJobListParams {
    appID: string
    page: number
    pageSize: number
}

interface GetJobHistoryArgs extends APIJobHistoryParams {
    jobID: string
    page: number
    pageSize: number
}

interface GetJobHistoryVersionResponseArgs extends APIEndpointFnArgs {
    jobID: string
    jobVersion: number
}

interface DownloadJobListArgs extends APIEndpointFnArgs {
    appID: string
    createdStartDate?: string
    createdEndDate?: string
    completedStartDate?: string
    completedEndDate?: string
    statusIn?: JobStatus[]
    correct?: boolean | null
    csv?: boolean
    reviewed?: JobReviewStatus[]
    jobIds?: string[]
    downloadSettings: AppDownloadSettings
    fileName?: string
}

interface GetGroundTruthListArgs extends APIEndpointFnArgs {
    appID: string
    page: number
    pageSize: number
    sort: SortExpression<APIGroundTruthModel>
    type?: APIGroundTruthType[]
}

interface GetCollabListArgs extends APIEndpointFnArgs {
    appID: string
    page: number
    pageSize: number
    sortBy: keyof APICollaborationModel
    desc: boolean
}

interface FeedbackMessagesArgs extends APIEndpointFnArgs {
    jobID: string
    page: number
    pageSize: number
    sortBy: FeedbackMessageSortBy
    orderBy: FeedbackMessageOrderBy
    ownerID?: string
}

interface GetTaskListArgs extends APITaskListParams {
    jobID: string
    page: number
    pageSize: number
}

interface TaskDetailsArgs extends APIEndpointFnArgs {
    jobID: string
    taskID: string
}

interface GetBackofficeCustomers extends BackofficeCustomerParams {
    size: number
    page: number
}

interface APIEndpoints {
    getOnboardingCode: APIRouteFn<any>
    checkOnboardingCode: APIRouteFn<CheckOnboardingCodeArgs>
    userBase: APIRouteFn<any>
    getJobList: APIRouteFn<GetJobListArgs>
    getJobCountByStatus: APIRouteFn<
        APIJobCountByStatusParams & { appID: string }
    >
    getJobByID: APIRouteFn<GetJobByIDArgs>
    getJobResponseByID: APIRouteFn<GetJobByIDArgs>
    getJobBigPayloadByID: APIRouteFn<GetJobByIDArgs>
    createJob: APIRouteFn<GetInstanceByIDArgs>
    createOnboardingJob: APIRouteFn<{ appID: string; code: number | string }>
    cancelJob: APIRouteFn<GetJobByIDArgs>
    cancelJobs: APIRouteFn<{ appID: string } & APIJobListParams>
    relabelJob: APIRouteFn<GetJobByIDArgs>
    relabelJobs: APIRouteFn<{ appID: string } & APIJobRelabelParams>
    getJobHistory: APIRouteFn<GetJobHistoryArgs>
    getJobHistoryVersionResponse: APIRouteFn<GetJobHistoryVersionResponseArgs>
    getTemplateList: APIRouteFn<any>
    getTemplateByName: APIRouteFn<GetTemplateByNameArgs>
    getTemplateByAppID: APIRouteFn<{ appID: string }>
    getInstanceByID: APIRouteFn<GetInstanceByIDArgs>
    getTaskDistributionByAppID: APIRouteFn<{ appID: string }>
    getInstanceList: APIRouteFn<GetInstanceListArgs>
    getOrgInstanceList: APIRouteFn<GetInstanceListArgs & { orgName: string }>
    getInstanceMetricsByID: APIRouteFn<GetInstanceByIDArgs>
    inbox: APIRouteFn<{ appID: string }>
    verifyInbox: APIRouteFn<{ appID: string }>
    getMetricsByAppID: APIRouteFn<{ appID: string }>
    getWeeklyQualityByAppID: APIRouteFn<{ appID: string }>
    getAllMetricsByAppID: APIRouteFn<{ appID: string }>
    createInstance: APIRouteFn<{ org: string | undefined }>
    cloneInstance: APIRouteFn<{ appUUID: string }>
    deletePaymentMethod: APIRouteFn<{ methodID: number }>
    baselineData: APIRouteFn<{ appID: string }>
    baselineDataList: APIRouteFn<GetGroundTruthListArgs>
    baselineDataFromJob: APIRouteFn<{ appID: string; jobID: number }>
    baseline: APIRouteFn<{ id: string }>
    completeOnboarding: APIRouteFn<{}>
    getAPIKeys: APIRouteFn<{}>
    createAPIKey: APIRouteFn<{ type?: "live" | "test" }>
    deleteAPIKey: APIRouteFn<{ apiKey: string }>
    createPaymentMethod: APIRouteFn<{}>
    getPaymentMethodList: APIRouteFn<{}>
    getInvoiceList: APIRouteFn<{}>
    getUploadDataUrl: APIRouteFn<{ uploadPath: string; fileType: string }>
    deleteData: APIRouteFn<{ path: string }>
    getDataList: APIRouteFn<{
        page?: number
        pageSize?: number
        sort: SortExpression<APIDataModel>
        includeSignedUrl: boolean
    }>
    getDataSignedUrl: APIRouteFn<{
        path: string
        jobId?: string
        baselineId?: string
        taskId?: string
        collabTaskId?: string
    }>
    downloadAllJobResponse: APIRouteFn<DownloadJobListArgs>
    getDownloadAllJobResponseLink: APIRouteFn<{ filename: string }>
    reviewJob: APIRouteFn<{
        jobID: string
        createsHistory?: boolean
        postProcess?: boolean
    }>
    collaboratorInvitationUserExists: APIRouteFn<{
        collaborationId: number
        code: string
    }>
    acceptInvitation: APIRouteFn<{ collaborationID: number; code: string }>
    getCollaborators: APIRouteFn<GetCollabListArgs>
    deleteCollaborator: APIRouteFn<{ collaborationID: number }>
    sendInvite: APIRouteFn<{}>
    getSetCollaborator: APIRouteFn<{ id: number }>
    listProcessingBatches: APIRouteFn<{ appUUID: string }>
    getProcessingBatch: APIRouteFn<{ appUUID: string; batchID: string }>
    listTags: APIRouteFn<{ appID: string; keyword: string }>
    enqueueBatch: APIRouteFn<{ appID: string } & APIJobEnqueueParams>
    enqueue: APIRouteFn<{ id: string }>
    deleteBatch: APIRouteFn<
        {
            appID: string
        } & APIJobListParams
    >
    deleteJob: APIRouteFn<{ id: string }>
    tagsByJobID: APIRouteFn<{ jobID: string }>
    tags: APIRouteFn<GetInstanceByIDArgs & APIJobListParams>
    contact: APIRouteFn<{}>
    listPlansMetadata: APIRouteFn<{ id: string }>
    metricConstraint: APIRouteFn<{
        page: number
        pageSize: number
        name?: string
    }>
    measurerConfiguration: APIRouteFn<{ id: string }>
    getFeedbackMessages: APIRouteFn<FeedbackMessagesArgs>
    createFeedbackMessage: APIRouteFn<{ jobID: string }>
    getFeedbackTags: APIRouteFn<{ appID: string }>
    getJobFeedbackMessageList: APIRouteFn<GetJobListArgs>
    getJobWithFeedbackStats: APIRouteFn<{ appUUID: string }>
    getFeedbackMessageViewState: APIRouteFn<{ jobID: string }>
    getJobWithMessageByID: APIRouteFn<GetJobByIDArgs>
    updateOwnerActivity: APIRouteFn<{ appUUID: string }>
    processAutoSegmentation: APIRouteFn<ScopedDataAccess>
    createObjectTrackingRequest: APIRouteFn<ScopedDataAccess>
    getObjectTrackingRequestStatus: APIRouteFn<{
        scope: ScopedDataAccess
        trackID: string
    }>
    getJobScoreExistsStatus: APIRouteFn<{ appUUID: string }>
    getJobIdsOfBatch: APIRouteFn<{ appUUID: string; batchID: string }>
    getTaskList: APIRouteFn<GetTaskListArgs>
    getTaskDetails: APIRouteFn<TaskDetailsArgs>
    reviewTask: APIRouteFn<TaskDetailsArgs>
    getSampleApp: APIRouteFn<{
        workflow: TemplateName
        sampleAppIdentifier: string
    }>
    getMyOrgs: APIRouteFn<{}>
    getOrgMembers: APIRouteFn<{
        orgUsername: string
        page: number
        pageSize: number
        sortBy: keyof APIOrgMember
        desc: boolean
    }>
    inviteToOrg: APIRouteFn<{ orgUsername: string }>
    updateMember: APIRouteFn<{ orgUsername: string; id: number }>
    removeMemberFromOrg: APIRouteFn<{ orgUsername: string; id: number }>
    acceptInvitationToOrg: APIRouteFn<{
        orgUsername: string
        memberId: number
        code: string
    }>
    orgInvitationUserExists: APIRouteFn<{
        orgUsername: string
        memberId: number
        code: string
    }>
    getWorkerList: APIRouteFn<{
        org?: string
        page: number
        pageSize: number
        sortBy: keyof APIWorker
        desc: boolean
    }>
    createWorker: APIRouteFn<{ org?: string }>
    acceptInvitationAsWorker: APIRouteFn<{
        workerId: number
    }>
    workerInvitationUserExists: APIRouteFn<{
        workerId: number
        code: string
    }>
    removeWorker: APIRouteFn<{ org?: string; id: number }>
    createWorkerGroup: APIRouteFn<{ org?: string }>
    patchWorkerGroup: APIRouteFn<{ org?: string; id: number }>
    removeWorkerGroup: APIRouteFn<{ org?: string; id: number }>
    getWorkerGroupList: APIRouteFn<{
        org?: string
        page: number
        pageSize: number
        sortBy: keyof APIWorkerGroup
        desc: boolean
    }>
    getAppWorkerList: APIRouteFn<{
        appID: string
        page: number
        pageSize: number
        sortBy: keyof APIAppWorker
        desc: boolean
    }>
    assignWorkerToApp: APIRouteFn<{ appID: string; id: number }>
    unassignWorkerFromApp: APIRouteFn<{ appID: string; id: number }>
    patchWorkerInApp: APIRouteFn<{ appID: string; id: number }>
    getAppWorkerGroupList: APIRouteFn<{
        appID: string
        page: number
        pageSize: number
        sortBy: keyof APIAppWorkerGroup
        desc: boolean
    }>
    getAppWorkerByUsername: APIRouteFn<{ appID: string; username: string }>
    assignWorkerGroupToApp: APIRouteFn<{ appID: string; id: number }>
    unassignWorkerGroupFromApp: APIRouteFn<{ appID: string; id: number }>
    patchWorkerGroupInApp: APIRouteFn<{ appID: string; id: number }>
    getAsyncOperations: APIRouteFn<{
        appID: string
        statusIn: APIAppAsyncOperationStatus[]
        typeIn: APIAppAsyncOperationType[]
    }>
    getAsyncOperation: APIRouteFn<{ appID: string; id: number }>
    retryAsyncOperation: APIRouteFn<{ appID: string; id: number }>
    getHero: APIRouteFn<{ id: number }>
    getTotalJobCount: APIRouteFn<{ appID: string }>
    // Backoffice
    getCustomers: APIRouteFn<GetBackofficeCustomers>
    getCustomer: APIRouteFn<{ username: string }>
    getCustomerAppList: APIRouteFn<
        GetInstanceListArgs & { customerEmail: string }
    >
    getMarketplaceAppByID: APIRouteFn<{ appID: string }>
    getSuperTasks: APIRouteFn<{ appID: string }>
    getOperationDownloadURL: APIRouteFn<{ appID: string; id: number }>
    getDetectedAnnotations: APIRouteFn<{}>
    getAppSchema: APIRouteFn<{}>
    // Client pulse
    getStatisticsSnapshot: APIRouteFn<{ appID: string }>
}

// TODO: handle options (RequestInit object)
export const apiEndpoints: APIEndpoints = Object.freeze<APIEndpoints>({
    getOnboardingCode: () => ({
        url: `/onboarding/code`
    }),
    checkOnboardingCode: ({ submitCode }) => ({
        url: `/onboarding/code/${submitCode}`
    }),
    userBase: () => ({
        url: `/users`,
        dontRequireAPIKey: true,
        requireAuthToken: true
    }),
    getJobList: ({ appID, page, pageSize, ...params }) => {
        const search = encodeJobAPIListParams(params)

        search.set("page", String(page))
        search.set("size", String(pageSize))

        return {
            url: `/apps/${appID}/jobs?${search.toString()}`
        }
    },
    getJobCountByStatus: ({ appID, ...params }) => {
        const search = encodeJobCountByStatusAPIParams(params)

        return {
            url: `/apps/${appID}/jobs/count?${search.toString()}`
        }
    },
    getJobByID: ({ jobID }) => ({
        url: `/jobs/${jobID}`
    }),
    getJobResponseByID: ({ jobID }) => ({
        url: `/jobs/${jobID}/response`
    }),
    getJobBigPayloadByID: ({ jobID }) => ({
        url: `/job-big-payload/${jobID}`
    }),
    createJob: ({ id }) => ({
        url: `/apps/${id}/jobs`
    }),
    createOnboardingJob: ({ appID, code }) => ({
        url: `/apps/${appID}/jobs?onboarding=${code}`
    }),
    cancelJob: ({ jobID }) => ({
        url: `/jobs/${jobID}/cancel`
    }),
    cancelJobs: ({ appID, ...params }) => ({
        url: `/apps/${appID}/cancel?${encodeJobCancelAPIParams(
            params
        ).toString()}`
    }),
    relabelJob: ({ jobID }) => ({
        url: `/jobs/${jobID}/relabel`
    }),
    getJobHistory: ({ jobID, page, pageSize, ...params }) => ({
        url: `/jobs/${jobID}/history?page=${page}&size=${pageSize}&sortBy=${params.sortBy}&orderBy=${params.orderBy}`
    }),
    getJobHistoryVersionResponse: ({ jobID, jobVersion }) => ({
        url: `/jobs/${jobID}/history/${jobVersion}/response`
    }),
    relabelJobs: ({ appID, ...params }) => ({
        url: `/apps/${appID}/relabel?${encodeJobRelabelAPIParams(
            params
        ).toString()}`
    }),
    getTemplateList: () => ({
        url: `/templates?page=0&size=1000`,
        useAPIKeyIfAvailable: true
    }),
    getTemplateByName: ({ templateName }) => ({
        url: `/templates/${templateName}`,
        useAPIKeyIfAvailable: true
    }),
    getTemplateByAppID: ({ appID }) => ({
        url: `/apps/${appID}/template`
    }),
    getInstanceByID: ({ id }) => ({
        url: `/apps/${id}`
    }),
    getMetricsByAppID: ({ appID }) => ({
        url: `/apps/${appID}/metrics`
    }),
    getWeeklyQualityByAppID: ({ appID }) => ({
        url: `/apps/${appID}/weekly_quality`
    }),
    getAllMetricsByAppID: ({ appID }) => ({
        url: `/apps/${appID}/all_metrics`
    }),
    getTaskDistributionByAppID: ({ appID }) => ({
        url: `/apps/${appID}/tasks-distribution`
    }),
    getInstanceList: ({ page, pageSize, ...params }) => {
        const search = encodeAPIAppInstanceListParams(params)

        search.set("page", String(page))
        search.set("size", String(pageSize))
        return {
            url: `/apps?${search.toString()}`
        }
    },
    getOrgInstanceList: ({ orgName, page, pageSize, ...params }) => {
        const search = encodeAPIAppInstanceListParams(params)

        search.set("page", String(page))
        search.set("size", String(pageSize))
        return {
            url: `/organizations/${orgName}/apps?${search.toString()}`
        }
    },
    createInstance: ({ org }) => ({
        url: `/apps${org === undefined ? "" : `?org=${org}`}`
    }),
    cloneInstance: ({ appUUID }) => ({
        url: `/apps/${appUUID}/clone`
    }),
    getInstanceMetricsByID: ({ id }) => ({
        url: `/apis/${id}/metrics`
    }),
    inbox: ({ appID }) => ({
        url: `/inbox/${appID}`
    }),
    verifyInbox: ({ appID }) => ({
        url: `/inbox/verify/${appID}`
    }),
    deletePaymentMethod: ({ methodID }) => ({
        url: `/billing/payments/${methodID}`
    }),
    baselineData: ({ appID }) => ({
        url: `/apis/${appID}/baselineData`
    }),
    baselineDataList: ({ appID, page, pageSize, sort, type }) => {
        const params = new URLSearchParams()

        params.set("page", String(page))
        params.set("size", String(pageSize))
        params.set("sortBy", sort.sortBy)
        params.set("orderBy", sort.desc ? "desc" : "asc")
        params.set("includeLabel", "false")

        if (type !== undefined) {
            type.forEach(s => params.append("type", s))
        }

        return {
            url: `/apis/${appID}/baselineData?${params.toString()}`
        }
    },
    baselineDataFromJob: ({ appID, jobID }) => ({
        url: `/apis/${appID}/baselineData/job/${jobID}`
    }),
    baseline: ({ id }) => ({
        url: `/baselineData/${id}`
    }),
    completeOnboarding: () => ({
        url: `/users/onboarded`
    }),
    getAPIKeys: () => ({
        url: `/users/apiKeys`,
        useAPIKeyIfAvailable: false,
        dontRequireAPIKey: true,
        requireAuthToken: true
    }),
    createAPIKey: ({ type = "live" }) => ({
        url: `/users/apiKeys/create?type=${type}`,
        useAPIKeyIfAvailable: false,
        dontRequireAPIKey: true,
        requireAuthToken: true
    }),
    deleteAPIKey: ({ apiKey }) => ({
        url: `/users/apiKeys/${apiKey}/delete`,
        useAPIKeyIfAvailable: false,
        dontRequireAPIKey: true,
        requireAuthToken: true
    }),
    createPaymentMethod: () => ({
        url: `/billing/payments`
    }),
    getPaymentMethodList: () => ({
        url: `/billing/payments?status=${APIPaymentMethodStatus.Active}`
    }),
    getInvoiceList: () => ({
        url: `/billing/invoices`
    }),
    getUploadDataUrl: ({ uploadPath, fileType }) => {
        const searchParams = new URLSearchParams()

        searchParams.set("uploadUrl", "true")
        searchParams.set("path", uploadPath)
        searchParams.set("mimeType", fileType)
        searchParams.set("secondsTtl", "600")

        return {
            url: `/data?${searchParams.toString()}`
        }
    },
    deleteData: ({ path }) => ({
        url: `/data?path=${path}`
    }),
    getDataList: ({ page, pageSize, sort, includeSignedUrl }) => {
        const searchParams = new URLSearchParams()

        if (includeSignedUrl) searchParams.set("signedUrl", "true")
        if (page !== undefined) searchParams.set("page", String(page))
        if (page !== undefined) searchParams.set("size", String(pageSize))

        searchParams.set("secondsTtl", "600")
        searchParams.set("sortBy", sort.sortBy)
        searchParams.set("orderBy", sort.desc ? "desc" : "asc")

        return {
            url: `/data?${searchParams.toString()}`
        }
    },
    getDataSignedUrl: ({ path, jobId, baselineId, taskId, collabTaskId }) => {
        const searchParams = new URLSearchParams()
        const ownerId = path.match(/^([\d]+)\//)
        const pathWithoutOwnerId = path.replace(/^([\d]+)\//, "")

        searchParams.set("path", pathWithoutOwnerId)
        if (ownerId !== null && ownerId[1])
            searchParams.set("ownerId", ownerId[1])
        if (jobId !== undefined) searchParams.set("jobId", jobId)
        if (baselineId !== undefined) searchParams.set("baselineId", baselineId)
        if (taskId !== undefined) searchParams.set("taskId", taskId)
        if (collabTaskId !== undefined)
            searchParams.set("collabTaskId", collabTaskId)
        searchParams.set("secondsTtl", "600")

        return {
            url: `/data/url?${searchParams.toString()}`
        }
    },
    downloadAllJobResponse: ({
        appID,
        csv,
        jobIds,
        downloadSettings,
        fileName,
        ...params
    }) => {
        const searchParams = encodeJobAPIListParams({
            ...params,
            sortBy: JobSortBy.id,
            orderBy: JobOrderBy.asc
        })

        const isFlatCsvOutput = csv && downloadSettings.expandOutputColumns

        if (!downloadSettings.sendEmail)
            searchParams.append("sendEmail", "false")
        if (csv) searchParams.append("csv", "true")

        searchParams.append(
            "flattenedOutput",
            `${downloadSettings.expandOutputColumns}`
        )

        if (downloadSettings.includeMessages)
            searchParams.append("withFeedback", "true")
        if (jobIds) {
            jobIds.forEach(jobId => searchParams.append("jobIds", jobId))
        }
        if (!downloadSettings.includeAnnotations && isFlatCsvOutput) {
            searchParams.append("annotationPostfixKeys", "content")
        }
        if (downloadSettings.flattenTables && isFlatCsvOutput) {
            searchParams.append("flattenTables", "true")
        }
        if (downloadSettings.excludeHeaderRow && csv) {
            searchParams.append("excludeHeaderRow", "true")
        }
        if (
            downloadSettings.fieldDelimiter &&
            downloadSettings.fieldDelimiter !== "," &&
            csv
        ) {
            searchParams.append(
                "fieldDelimiter",
                downloadSettings.fieldDelimiter
            )
        }
        if (downloadSettings.quoteValues && csv) {
            searchParams.append("quoteValues", "true")
        }
        if (fileName) {
            searchParams.append("fileName", fileName)
        }
        return {
            url: `/apps/${appID}/job_responses?${searchParams.toString()}`
        }
    },
    getDownloadAllJobResponseLink: ({ filename }) => ({
        url: `/apps/job_responses_link/${filename}`
    }),
    reviewJob: ({ jobID, createsHistory = true, postProcess = true }) => ({
        url: `/jobs/${jobID}/review?createsHistory=${createsHistory}&postProcess=${postProcess}`
    }),
    collaboratorInvitationUserExists: ({ collaborationId, code }) => ({
        url: `/collaborators/${collaborationId}/user-exists?verification_code=${code}`,
        dontRequireAPIKey: true
    }),
    acceptInvitation: ({ collaborationID, code }) => ({
        url: `/collaborators/${collaborationID}/accept?verificationCode=${code}`
    }),
    getCollaborators: ({ appID, desc, page, pageSize, sortBy }) => {
        const searchParams = new URLSearchParams()
        searchParams.set("page", String(page))
        searchParams.set("size", String(pageSize))
        searchParams.set("sortBy", sortBy)
        searchParams.set("orderBy", desc ? "desc" : "asc")

        return {
            url: `/collaborators/app/${appID}?${searchParams.toString()}`
        }
    },
    deleteCollaborator: ({ collaborationID }) => ({
        url: `/collaborators/${collaborationID}`
    }),
    sendInvite: () => ({
        url: `/collaborators`
    }),
    getSetCollaborator: ({ id }) => ({
        url: `/collaborators/${id}`
    }),
    listProcessingBatches: ({ appUUID }) => ({
        url: `/apps/${appUUID}/batches`
    }),
    getProcessingBatch: ({ appUUID, batchID }) => ({
        url: `/apps/${appUUID}/batches/${batchID}`
    }),
    listTags: ({ appID, keyword }) => ({
        url: `/app/apps/${appID}/tags${
            keyword === "" ? "" : `?tagContains=${encodeURIComponent(keyword)}`
        }`
    }),
    enqueueBatch: ({ appID, ...params }) => ({
        url: `/apps/${appID}/start?${encodeJobEnqueueAPIParams(
            params
        ).toString()}`
    }),
    enqueue: ({ id }) => ({ url: `/jobs/${id}/start` }),
    deleteBatch: ({ appID, ...params }) => {
        const searchParams = encodeJobAPIListParams(params)
        searchParams.delete("sortBy")
        searchParams.delete("orderBy")
        return {
            url: `/apps/${appID}/delete_jobs?${searchParams.toString()}`
        }
    },
    deleteJob: ({ id }) => ({ url: `/jobs/${id}` }),
    tagsByJobID: ({ jobID }) => ({ url: `/jobs/${jobID}/tags` }),
    tags: ({ id, ...params }) => ({
        url: `/apps/${id}/jobs/tags?${encodeJobAPIListParams(params)}`
    }),
    contact: () => ({ url: `/contact` }),
    listPlansMetadata: ({ id }) => ({ url: `/apps/${id}/plans_metadata` }),
    metricConstraint: ({ page, pageSize, name }) => {
        const params = new URLSearchParams(
            [
                ["page", String(page)],
                ["size", String(pageSize)],
                ["sortBy", "name"],
                ["orderBy", "asc"]
            ].concat(name === undefined || name === "" ? [] : [["name", name]])
        )

        return {
            url: `/metric_constraint?${params.toString()}`
        }
    },
    measurerConfiguration: ({ id }) => ({
        url: `/apps/${id}/measurer/configuration`
    }),
    getFeedbackMessages: ({
        jobID,
        page,
        pageSize,
        sortBy,
        orderBy,
        ownerID
    }) => ({
        url: `/feedbackmessages/${jobID}?page=${page}&size=${pageSize}&sortBy=${sortBy}&orderBy=${orderBy}${
            ownerID ? "&ownerId=" + ownerID : ""
        }`
    }),
    createFeedbackMessage: ({ jobID }) => ({
        url: `/feedbackmessages/${jobID}`
    }),
    getFeedbackTags: ({ appID }) => ({
        url: `/apps/${appID}/feedbacktags`
    }),
    getJobFeedbackMessageList: ({ appID, page, pageSize, ...params }) => {
        const search = encodeJobAPIListParams(params)

        search.set("page", String(page))
        search.set("size", String(pageSize))

        return {
            url: `/apps/${appID}/jobs/with-feedback-message?${search.toString()}`
        }
    },
    getJobWithFeedbackStats: ({ appUUID }) => ({
        url: `/apps/${appUUID}/job-feedback-stats`
    }),
    getFeedbackMessageViewState: ({ jobID }) => ({
        url: `/feedbackmessages/viewstate/${jobID}`
    }),
    getJobWithMessageByID: ({ jobID }) => ({
        url: `/jobs/with-message/${jobID}`
    }),
    updateOwnerActivity: ({ appUUID }) => ({
        url: `/apps/owner-activity/${appUUID}`
    }),
    processAutoSegmentation: dataAccess => ({
        service: "autoSegmentation",
        url: `/v1/instance-segmentation/prediction/${
            dataAccess.type === "perTask"
                ? dataAccess.taskID
                : dataAccess.type === "perCollabTask"
                ? dataAccess.collabTaskId
                : dataAccess.type === "perGT"
                ? `baseline/${dataAccess.baselineID}`
                : `job/${dataAccess.jobID}`
        }`
    }),
    createObjectTrackingRequest: scope => ({
        service: "objectTracking",
        url: `/v1/video-tracking/prediction/${
            scope.type === "perTask"
                ? scope.taskID
                : scope.type === "perCollabTask"
                ? scope.collabTaskId
                : scope.type === "perGT"
                ? `baseline/${scope.baselineID}`
                : `job/${scope.jobID}`
        }`
    }),
    getObjectTrackingRequestStatus: ({ scope, trackID }) => ({
        service: "objectTracking",
        url: `/v1/video-tracking/prediction/${
            scope.type === "perTask"
                ? scope.taskID
                : scope.type === "perCollabTask"
                ? scope.collabTaskId
                : scope.type === "perGT"
                ? `baseline/${scope.baselineID}`
                : `job/${scope.jobID}`
        }?track_uuid=${trackID}`
    }),
    getJobScoreExistsStatus: ({ appUUID }) => ({
        url: `/apps/job-score-exists/${appUUID}`
    }),
    getJobIdsOfBatch: ({ appUUID, batchID }) => ({
        url: `/apps/${appUUID}/batches/${batchID}/ids`
    }),
    getTaskList: ({ jobID, page, pageSize, ...params }) => {
        const search = encodeTaskAPIListParams(params)

        search.set("page", String(page))
        search.set("size", String(pageSize))

        return {
            url: `/jobs/${jobID}/tasks?${search.toString()}`
        }
    },
    getTaskDetails: ({ jobID, taskID }) => ({
        url: `/jobs/${jobID}/tasks/${taskID}`
    }),
    reviewTask: ({ jobID, taskID }) => ({
        url: `/jobs/${jobID}/tasks/${taskID}/review`
    }),
    getSampleApp: ({ workflow, sampleAppIdentifier }) => {
        const search = new URLSearchParams()
        search.set("sampleAppIdentifier", sampleAppIdentifier)
        return {
            url: `/templates/${workflow}/sample-app?${search.toString()}`
        }
    },
    getMyOrgs: () => ({
        url: `/organizations`
    }),
    getOrgMembers: ({ orgUsername, page, pageSize, sortBy, desc }) => {
        const params = new URLSearchParams([
            ["page", String(page)],
            ["size", String(pageSize)],
            ["orderBy", desc ? "desc" : "asc"],
            ["sortBy", sortBy]
        ])

        return {
            url: `/organizations/${orgUsername}/members?${params.toString()}`
        }
    },
    inviteToOrg: ({ orgUsername }) => ({
        url: `/organizations/${orgUsername}/invite`
    }),
    updateMember: ({ orgUsername, id }) => ({
        url: `/organizations/${orgUsername}/members/${id}`
    }),
    removeMemberFromOrg: ({ orgUsername, id }) => ({
        url: `/organizations/${orgUsername}/members/${id}`
    }),
    acceptInvitationToOrg: ({ orgUsername, memberId, code }) => ({
        url: `/organizations/${orgUsername}/invite/${memberId}/accept?code=${code}`
    }),
    orgInvitationUserExists: ({ orgUsername, memberId, code }) => ({
        url: `/organizations/${orgUsername}/invite/${memberId}/user-exists?verification_code=${code}`,
        dontRequireAPIKey: true
    }),
    getWorkerList: ({ org, page, pageSize, desc, sortBy }) => {
        const params = new URLSearchParams(
            [
                ["page", String(page)],
                ["size", String(pageSize)],
                ["orderBy", desc ? "desc" : "asc"],
                ["sortBy", sortBy]
            ].concat(org === undefined ? [] : [["org", org]])
        )

        return { url: `/workers?${params.toString()}` }
    },
    createWorker: ({ org }) => ({
        url: `/workers${org === undefined ? "" : `?org=${org}`}`
    }),
    acceptInvitationAsWorker: ({ workerId }) => ({
        url: `/workers/${workerId}/accept`
    }),
    workerInvitationUserExists: ({ code, workerId }) => ({
        url: `/workers/${workerId}/user-exists?verification_code=${code}`,
        dontRequireAPIKey: true
    }),
    removeWorker: ({ org, id }) => ({
        url: `/workers/${id}${org === undefined ? "" : `?org=${org}`}`
    }),
    createWorkerGroup: ({ org }) => ({
        url: `/worker-groups${org === undefined ? "" : `?org=${org}`}`
    }),
    patchWorkerGroup: ({ org, id }) => ({
        url: `/worker-groups/${id}${org === undefined ? "" : `?org=${org}`}`
    }),
    removeWorkerGroup: ({ org, id }) => ({
        url: `/worker-groups/${id}${org === undefined ? "" : `?org=${org}`}`
    }),
    getWorkerGroupList: ({ org, page, pageSize, desc, sortBy }) => {
        const params = new URLSearchParams(
            [
                ["page", String(page)],
                ["size", String(pageSize)],
                ["orderBy", desc ? "desc" : "asc"],
                ["sortBy", sortBy]
            ].concat(org === undefined ? [] : [["org", org]])
        )

        return { url: `/worker-groups?${params.toString()}` }
    },
    getAppWorkerList: ({ appID, page, pageSize, desc, sortBy }) => {
        const params = new URLSearchParams([
            ["page", String(page)],
            ["size", String(pageSize)],
            ["orderBy", desc ? "desc" : "asc"],
            ["sortBy", sortBy]
        ])

        return { url: `/app/${appID}/workers?${params.toString()}` }
    },
    getAppWorkerByUsername: ({ appID, username }) => ({
        url: `/app/${appID}/workers/by-username/${username}`
    }),
    assignWorkerToApp: ({ appID, id }) => ({
        url: `/app/${appID}/workers/${id}`
    }),
    unassignWorkerFromApp: ({ appID, id }) => ({
        url: `/app/${appID}/workers/${id}`
    }),
    patchWorkerInApp: ({ appID, id }) => ({
        url: `/app/${appID}/workers/${id}`
    }),
    getAppWorkerGroupList: ({ appID, page, pageSize, desc, sortBy }) => {
        const params = new URLSearchParams([
            ["page", String(page)],
            ["size", String(pageSize)],
            ["orderBy", desc ? "desc" : "asc"],
            ["sortBy", sortBy]
        ])

        return { url: `/app/${appID}/worker-groups?${params.toString()}` }
    },
    assignWorkerGroupToApp: ({ appID, id }) => ({
        url: `/app/${appID}/worker-groups/${id}`
    }),
    unassignWorkerGroupFromApp: ({ appID, id }) => ({
        url: `/app/${appID}/worker-groups/${id}`
    }),
    patchWorkerGroupInApp: ({ appID, id }) => ({
        url: `/app/${appID}/worker-groups/${id}`
    }),
    getAsyncOperations: ({ appID, typeIn, statusIn }) => ({
        url: `/operations/${appID}?${typeIn
            .map(t => `typeIn=${t}`)
            .concat(statusIn.map(s => `statusIn=${s}`))
            .join("&")}`
    }),
    getAsyncOperation: ({ appID, id }) => ({
        url: `/operations/${appID}/${id}`
    }),
    retryAsyncOperation: ({ appID, id }) => ({
        url: `/operations/${appID}/${id}/retry`
    }),
    getHero: ({ id }) => ({ url: `/heroes/${id}` }),
    getTotalJobCount: ({ appID }) => ({
        url: `/apps/${appID}/total_job_count`
    }),

    getCustomers: ({ size, page, ...params }) => {
        const searchParams = encodeBackofficeParams(params)
        searchParams.set("page", String(page))
        searchParams.set("size", String(size))
        return {
            url: `/backoffice/customers?${searchParams.toString()}`,
            requireAuthToken: true
        }
    },
    getCustomer: ({ username }) => ({
        url: `/backoffice/customers/${username}`,
        requireAuthToken: true
    }),
    getCustomerAppList: ({ customerEmail, page, pageSize, ...params }) => {
        const search = encodeAPIAppInstanceListParams(params)

        search.set("page", String(page))
        search.set("size", String(pageSize))
        return {
            url: `/backoffice/customers/${customerEmail}/apps?${search.toString()}`,
            requireAuthToken: true
        }
    },
    getMarketplaceAppByID: ({ appID }) => ({
        url: `/apps/${appID}`,
        useAPIKeyIfAvailable: true
    }),
    getSuperTasks: ({ appID }) => ({
        url: `/apps/${appID}/super-tasks`
    }),
    getOperationDownloadURL: ({ appID, id }) => ({
        url: `/operations/${appID}/${id}/download-url`
    }),
    getDetectedAnnotations: () => ({
        url: `/some-url` // TODO: TBD
    }),
    getAppSchema: () => ({
        url: "/schema"
    }),
    getStatisticsSnapshot: ({ appID }) => ({
        url: `/create-snapshot?app_uuid=${appID}`,
        service: "clientPulse"
    })
})
